
import React, { useState, useEffect, useCallback, useLayoutEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import AddButton from "../../Components/CommonComponent/Button/AddButton";
import Pagination, { usePagination } from "../../Components/CommonComponent/Pgination/pagination";
import AddInventory from "./AddInventory";
import Navbar from "../../Components/Navbar/Navbar";
import { useContext } from "react";
import { CommonContext } from "../../App";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import { FaEdit } from 'react-icons/fa';
import { FaEye } from "react-icons/fa";
import { RiDeleteBin6Fill } from 'react-icons/ri';
import ViewInventoryModal from "./ViewInventoryDetails";
import { useTranslation } from "react-i18next";
import { IoArrowUndoSharp } from "react-icons/io5";
import { NavLink, useLocation, useNavigate } from "react-router";
import api from "../../services/api";

export default function InventoryTable() {
  const [inventory, setInventory] = useState({ allData: [], pagination: { totalRecords: 0 } });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLedger, setSelectedLedger] = useState(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const { t } = useTranslation();
  const { currentPage, itemsPerPage, handlePageChange, handleItemsPerPageChange, getPaginatedData } =
    usePagination(10);
  const location = useLocation();

  // ✅ Now also receives stockTypeFilter from StockTypeSubGroupTable
  const { category, stockTypeFilter } = location.state || {};
  const paginationData = getPaginatedData(inventory);
  const { branchId, dateRange } = useContext(CommonContext);
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const containerRef = React.useRef(null);
  const rowRefs = React.useRef([]);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const navigate = useNavigate();

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setStartDate(dateRange.startDate);
      setEndDate(dateRange.endDate);
    }
  }, [dateRange]);

  const handleKeyDown = useCallback((e) => {
    const data = paginationData.currentItems;
    if (!data?.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) => prev === -1 ? 0 : Math.min(prev + 1, data.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => prev === -1 ? data.length - 1 : Math.max(prev - 1, 0));
    }
    if (e.key === " ") {
      e.preventDefault();
      if (focusedIndex >= 0) {
        const id = data[focusedIndex].ledger_id;
        setSelectedRows((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(id)) newSet.delete(id);
          else newSet.add(id);
          return newSet;
        });
      }
    }
    if (e.key === "Enter") {
      if (focusedIndex >= 0) handleViewDetails(data[focusedIndex]);
    }
  }, [paginationData.currentItems, focusedIndex]);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleGlobalKey = (e) => {
      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        if (document.activeElement !== containerRef.current) {
          containerRef.current?.focus();
          handleKeyDown(e);
        }
      }
      if (e.key === "Escape") {
        // ✅ ESC goes back to StockTypeSubGroupTable (new intermediate step)
        navigate("/inventory-books/sub-category-table", { state: { category } });
      }
    };

    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, [handleKeyDown, navigate, category]);

  useLayoutEffect(() => {
    if (focusedIndex < 0) return;
    const row = rowRefs.current[focusedIndex];
    const container = containerRef.current;
    if (!row || !container) return;

    const containerHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    const rowTop = row.offsetTop;
    const rowHeight = row.offsetHeight;
    const rowCenter = rowTop + rowHeight / 2;
    const containerCenter = scrollTop + containerHeight / 2;
    const OFFSET = 60;

    if (rowCenter > containerCenter + OFFSET) {
      container.scrollTo({ top: rowTop - containerHeight / 2 + rowHeight, behavior: "smooth" });
    } else if (rowCenter < containerCenter - OFFSET) {
      container.scrollTo({ top: rowTop - containerHeight / 2, behavior: "smooth" });
    }
  }, [focusedIndex]);

  useEffect(() => {
    setFocusedIndex(-1);
    setSelectedRows(new Set());
  }, [currentPage, itemsPerPage]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `inventory/get-all-item?branchId=${branchId}&startDate=${startDate}&endDate=${endDate}`
      );

      if (res.data.success) {
        const finalArray = Array.isArray(res.data.data)
          ? res.data.data
          : res.data.data?.allData || [];

        // Filter by parent category (group_id)
        let filterData = finalArray.filter(
          (item) => item.group_id === (category?.groupId || category?.group_id)
        );

        // ✅ Additionally filter by stock type if coming from StockTypeSubGroupTable
        if (stockTypeFilter) {
          filterData = filterData.filter((item) => item.groupType === stockTypeFilter);
        }

        setInventory({
          allData: filterData,
          pagination: { totalRecords: filterData.length },
        });

        return filterData;
      }
      return [];
    } catch (err) {
      toast.error("Error fetching inventory");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchId || !startDate || !endDate) return;
    fetchInventory();
  }, [branchId, startDate, endDate]);

  const handleAddInventory = async (form, id = null) => {
    try {
      setLoading(true);

      let res;

      if (id) {
        res = await api.post(`inventory/update/${id}`, form);

        if (res.data.success) {
          toast.success("Updated successfully");
          setIsModalOpen(false);
          setEditItem(null);

          const updatedInventory = await fetchInventory();
          const updatedItem = updatedInventory.find(
            (item) => item.ledger_id === Number(form.ledger_id)
          );

          setViewData(updatedItem);
          setViewModalOpen(true);
        }
      } else {
        res = await api.post(`inventory/add-new`, {
          ...form,
          branch_id: branchId,
        });
        if (res.data.success) {
          toast.success("Added successfully");
          setIsModalOpen(false);
          fetchInventory();
        }
      }
    } catch (error) {
      toast.error("Error in create & update item");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (row) => {
    navigate("/inventory-books/item-details", {
      state: { category: row, branchId, endDate, startDate },
    });
  };

  return (
    <div className="flex justify-center">
      <div className="w-full flex flex-col">
        <div className="px-1 p-1 mt-1 sticky top-0">
          {/* ✅ Back button now goes to StockTypeSubGroupTable */}
          <NavLink
            to="/inventory-books/sub-category-table"
            title="Back to stock types"
            state={{ category }}
            className="inline-block p-1 px-2 text-theme-backBtn border-1 border-theme-backBtn rounded cursor-pointer hover:bg-theme-backBtn hover:text-theme-color"
          >
            <IoArrowUndoSharp />
          </NavLink>
        </div>

        <div>
          <div className="flex justify-between items-center py-[2px] bg-theme-headerBg px-2 text-theme-color">
            <div className="font-medium">
              {t("items")}
              {/* ✅ Show stock type filter label if present */}
              {stockTypeFilter && (
                <span className="ml-2 text-xs opacity-75">({stockTypeFilter})</span>
              )}
            </div>
            <AddButton text={t("updateStock")} onClick={() => setIsModalOpen(true)} />
          </div>

          <div>
            {loading ? (
              <div className="flex justify-center my-4">
                <LoadingSpinner size={70} color="#000000" />
              </div>
            ) : inventory.allData.length === 0 ? (
              <p className="text-center text-gray-500 italic text-sm py-4">{t("noDataAvailable")}</p>
            ) : (
              <div
                ref={containerRef}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                className="overflow-x-auto outline-none"
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("srNumber")}</th>
                      <th className="border-b py-[2px] px-1 text-start font-medium">{t("name")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("openingStock")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("quantityIn")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("amountIn")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("quantityOut")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("amountOut")}</th>
                      <th className="border-b py-[2px] px-1 text-center font-medium">{t("closingStock")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginationData.currentItems.map((row, i) => {
                      const isFocused = i === focusedIndex;
                      const isSelected = selectedRows.has(row.ledger_id);

                      return (
                        <tr
                          key={row.ledger_id}
                          ref={(el) => (rowRefs.current[i] = el)}
                          onClick={() => {
                            setFocusedIndex(i);
                            handleViewDetails(row);
                          }}
                          className={`
                            cursor-pointer transition-all duration-150
                            ${
                              isFocused
                                ? "bg-[#5c5b5b] text-white"
                                : isSelected
                                ? "bg-[#f4c48a]"
                                : "bg-theme-tableBg"
                            }
                          `}
                        >
                          <td className="border-b py-[2px] px-1 text-center">
                            {(currentPage - 1) * itemsPerPage + i + 1}
                          </td>
                          <td className="border-b py-[2px] px-1 text-start">
                            {row.ledger_name}{" "}
                            {row.ledgerNameInHindi && row.ledgerNameInHindi}
                          </td>
                          <td className="border-b py-[2px] px-1 text-center">{row.opening_stock}</td>
                          <td className="border-b py-[2px] px-1 text-center">{row.in_side}</td>
                          <td className="border-b py-[2px] px-1 text-center">{row.total_in_price}</td>
                          <td className="border-b py-[2px] px-1 text-center">{row.out_side}</td>
                          <td className="border-b py-[2px] px-1 text-center">{row.total_out_price}</td>
                          <td className="border-b py-[2px] px-1 text-center">
                            {row.opening_stock + row.in_side - row.out_side}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <Pagination
                  data={inventory}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemsPerPageOptions={[10, 20, 50]}
                  showPageInfo
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <AddInventory
            editItem={editItem}
            handleSave={handleAddInventory}
            handleCancel={() => {
              setIsModalOpen(false);
              setEditItem(null);
            }}
            loading={loading}
          />
        </div>
      )}
      {viewModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
          <ViewInventoryModal
            data={viewData}
            setEditItem={setEditItem}
            onClose={() => setViewModalOpen(false)}
            openEditModal={() => setIsModalOpen(true)}
          />
        </div>
      )}
    </div>
  );
}